// Sanitized request-failure metadata. Only fields below ever leave the
// browser: HTTP method, endpoint pathname (no origin/query/hash), elapsed ms,
// a fixed error category, the actual HTTP status and an existing W3C trace id.
// Never add headers, bodies, params, tokens, error text or response data here.

const MAX_PATH = 200;
const TRACEPARENT = /^[\da-f]{2}-([\da-f]{32})-[\da-f]{16}-[\da-f]{2}$/i;

export const FAILURE_CATEGORY = Object.freeze({
  ACCESS_DENIED: 'access_denied',
  SESSION: 'session',
  NETWORK: 'network',
  HTTP: 'http_error',
  INVALID_RESPONSE: 'invalid_response',
  BACKEND: 'backend_error',
  CLIENT: 'client',
});

export const now = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now());

export function endpointPath(url) {
  if (typeof url !== 'string') return '';
  try {
    return new URL(url, 'https://placeholder.invalid').pathname.slice(0, MAX_PATH);
  } catch (_) {
    return '';
  }
}

// The fetch instrumentation, when enabled and allowed for this origin, injects
// `traceparent` into the init headers we passed. Read it back; never create one.
export function traceIdFromInit(init) {
  try {
    const headers = init?.headers;
    const value = headers && typeof headers.get === 'function' ? headers.get('traceparent') : headers?.traceparent;
    return TRACEPARENT.exec(value || '')?.[1]?.toLowerCase();
  } catch (_) {
    return undefined;
  }
}

// `trace` is the mutable record requestForDocument fills while it runs:
// { method, url, startedAt, stage, status, traceId }.
export function describeFailure(error, trace) {
  const stage = trace.stage;
  let category = FAILURE_CATEGORY.CLIENT;
  if (error?.accessDenied) category = FAILURE_CATEGORY.ACCESS_DENIED;
  else if (stage === 'session') category = FAILURE_CATEGORY.SESSION;
  else if (stage === 'fetch') category = FAILURE_CATEGORY.NETWORK;
  else if (stage === 'parse') category = FAILURE_CATEGORY.INVALID_RESPONSE;
  else if (stage === 'http') category = FAILURE_CATEGORY.HTTP;
  else if (stage === 'unwrap') category = FAILURE_CATEGORY.BACKEND;
  const out = {
    method: String(trace.method || 'POST').toUpperCase().slice(0, 10),
    path: endpointPath(trace.url),
    durationMs: Math.max(0, Math.round(now() - trace.startedAt)),
    category,
  };
  if (Number.isInteger(trace.status)) out.status = trace.status;
  if (trace.traceId) out.traceId = trace.traceId;
  return out;
}
